
// src/services/features/visitReport/visitReportSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../../API/api';

const msg = (e) => e.response?.data?.message || e.message;

// ── Thunks ────────────────────────────────────────────────────────
export const createVisitReport = createAsyncThunk(
  'visitReport/create',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await API.post('/api/visit-reports', payload);
      return data.report;
    } catch (e) {
      return rejectWithValue(msg(e));
    }
  }
);

export const fetchMyReports = createAsyncThunk(
  'visitReport/fetchMine',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await API.get('/api/visit-reports/my-history', { params });
      return data;
    } catch (e) {
      return rejectWithValue(msg(e));
    }
  }
);

export const fetchAllReports = createAsyncThunk(
  'visitReport/fetchAll',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await API.get('/api/visit-reports', { params });
      return data;
    } catch (e) {
      return rejectWithValue(msg(e));
    }
  }
);

// One row per manager / executive: totals, Hot/Warm/Cold, customers, last report
export const fetchExecutiveSummary = createAsyncThunk(
  'visitReport/fetchExecutiveSummary',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await API.get('/api/visit-reports/executive-summary', { params });
      return data;
    } catch (e) {
      return rejectWithValue(msg(e));
    }
  }
);

// Duplicate / revisit check — mobile, customer name and shop name separately
export const checkDuplicateVisits = createAsyncThunk(
  'visitReport/checkDuplicate',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await API.get('/api/visit-reports/check-duplicate', { params });
      return data;
    } catch (e) {
      return rejectWithValue(msg(e));
    }
  }
);

export const fetchReportById = createAsyncThunk(
  'visitReport/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await API.get(`/api/visit-reports/${id}`);
      return data.report;
    } catch (e) {
      return rejectWithValue(msg(e));
    }
  }
);

export const updateVisitReport = createAsyncThunk(
  'visitReport/update',
  async ({ id, data: body }, { rejectWithValue }) => {
    try {
      const { data } = await API.put(`/api/visit-reports/${id}`, body);
      return data.report;
    } catch (e) {
      return rejectWithValue(msg(e));
    }
  }
);

export const deleteVisitReport = createAsyncThunk(
  'visitReport/delete',
  async (id, { rejectWithValue }) => {
    try {
      await API.delete(`/api/visit-reports/${id}`);
      return id;
    } catch (e) {
      return rejectWithValue(msg(e));
    }
  }
);

// ── Slice ─────────────────────────────────────────────────────────
const slice = createSlice({
  name: 'visitReport',
  initialState: {
    myList: [],
    allList: [],
    myTotal: 0,
    allTotal: 0,
    execSummary: null,
    selected: null,
    loading: false,       // lists
    summaryLoading: false, // dashboard summary
    submitting: false,
    error: null,
  },
  reducers: {
    clearVisitReportError: (s) => {
      s.error = null;
    },
    clearSelected: (s) => {
      s.selected = null;
    },
  },
  extraReducers: (b) => {
    b
      // create
      .addCase(createVisitReport.pending, (s) => {
        s.submitting = true;
        s.error = null;
      })
      .addCase(createVisitReport.fulfilled, (s, a) => {
        s.submitting = false;
        s.myList = [a.payload, ...s.myList];
      })
      .addCase(createVisitReport.rejected, (s, a) => {
        s.submitting = false;
        s.error = a.payload;
      })

      // my history
      .addCase(fetchMyReports.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(fetchMyReports.fulfilled, (s, a) => {
        s.loading = false;
        s.myList = a.payload.items || [];
        s.myTotal = a.payload.total ?? (a.payload.items || []).length;
      })
      .addCase(fetchMyReports.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload;
      })

      // all / team
      .addCase(fetchAllReports.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(fetchAllReports.fulfilled, (s, a) => {
        s.loading = false;
        s.allList = a.payload.items || [];
        s.allTotal = a.payload.total ?? (a.payload.items || []).length;
      })
      .addCase(fetchAllReports.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload;
      })

      // per-executive summary
      .addCase(fetchExecutiveSummary.pending, (s) => {
        s.summaryLoading = true;
      })
      .addCase(fetchExecutiveSummary.fulfilled, (s, a) => {
        s.summaryLoading = false;
        s.execSummary = a.payload;
      })
      .addCase(fetchExecutiveSummary.rejected, (s, a) => {
        s.summaryLoading = false;
        s.error = a.payload;
      })

      // by id
      .addCase(fetchReportById.fulfilled, (s, a) => {
        s.selected = a.payload;
      })

      // update
      .addCase(updateVisitReport.fulfilled, (s, a) => {
        s.selected = a.payload;
        const i = s.myList.findIndex((x) => x._id === a.payload._id);
        if (i !== -1) s.myList[i] = a.payload;
        const j = s.allList.findIndex((x) => x._id === a.payload._id);
        if (j !== -1) s.allList[j] = a.payload;
      })

      // delete
      .addCase(deleteVisitReport.fulfilled, (s, a) => {
        s.myList = s.myList.filter((x) => x._id !== a.payload);
        s.allList = s.allList.filter((x) => x._id !== a.payload);
      });
  },
});

export const { clearVisitReportError, clearSelected } = slice.actions;
export default slice.reducer;


//------------ 13.10.2026 Backup -------------
// // src/services/features/visitReport/visitReportSlice.js
// import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
// import API from '../../API/api';

// const msg = (e) => e.response?.data?.message || e.message;

// // ── Thunks ────────────────────────────────────────────────────────
// export const createVisitReport = createAsyncThunk(
//   'visitReport/create',
//   async (payload, { rejectWithValue }) => {
//     try {
//       const { data } = await API.post('/api/visit-reports', payload);
//       return data.report;
//     } catch (e) {
//       return rejectWithValue(msg(e));
//     }
//   }
// );

// export const fetchMyReports = createAsyncThunk(
//   'visitReport/fetchMine',
//   async (params = {}, { rejectWithValue }) => {
//     try {
//       const { data } = await API.get('/api/visit-reports/my-history', { params });
//       return data;
//     } catch (e) {
//       return rejectWithValue(msg(e));
//     }
//   }
// );

// export const fetchAllReports = createAsyncThunk(
//   'visitReport/fetchAll',
//   async (params = {}, { rejectWithValue }) => {
//     try {
//       const { data } = await API.get('/api/visit-reports', { params });
//       return data;
//     } catch (e) {
//       return rejectWithValue(msg(e));
//     }
//   }
// );

// // One row per manager / executive: totals, Hot/Warm/Cold, customers, last report
// export const fetchExecutiveSummary = createAsyncThunk(
//   'visitReport/fetchExecutiveSummary',
//   async (params = {}, { rejectWithValue }) => {
//     try {
//       const { data } = await API.get('/api/visit-reports/executive-summary', { params });
//       return data;
//     } catch (e) {
//       return rejectWithValue(msg(e));
//     }
//   }
// );

// // Duplicate / revisit check — mobile, customer name and shop name separately
// export const checkDuplicateVisits = createAsyncThunk(
//   'visitReport/checkDuplicate',
//   async (params, { rejectWithValue }) => {
//     try {
//       const { data } = await API.get('/api/visit-reports/check-duplicate', { params });
//       return data;
//     } catch (e) {
//       return rejectWithValue(msg(e));
//     }
//   }
// );

// export const fetchReportById = createAsyncThunk(
//   'visitReport/fetchById',
//   async (id, { rejectWithValue }) => {
//     try {
//       const { data } = await API.get(`/api/visit-reports/${id}`);
//       return data.report;
//     } catch (e) {
//       return rejectWithValue(msg(e));
//     }
//   }
// );

// export const updateVisitReport = createAsyncThunk(
//   'visitReport/update',
//   async ({ id, data: body }, { rejectWithValue }) => {
//     try {
//       const { data } = await API.put(`/api/visit-reports/${id}`, body);
//       return data.report;
//     } catch (e) {
//       return rejectWithValue(msg(e));
//     }
//   }
// );

// export const deleteVisitReport = createAsyncThunk(
//   'visitReport/delete',
//   async (id, { rejectWithValue }) => {
//     try {
//       await API.delete(`/api/visit-reports/${id}`);
//       return id;
//     } catch (e) {
//       return rejectWithValue(msg(e));
//     }
//   }
// );

// // ── Slice ─────────────────────────────────────────────────────────
// const slice = createSlice({
//   name: 'visitReport',
//   initialState: {
//     myList: [],
//     allList: [],
//     myTotal: 0,
//     allTotal: 0,
//     execSummary: null,
//     selected: null,
//     loading: false,       // lists
//     summaryLoading: false, // dashboard summary
//     submitting: false,
//     error: null,
//   },
//   reducers: {
//     clearVisitReportError: (s) => {
//       s.error = null;
//     },
//     clearSelected: (s) => {
//       s.selected = null;
//     },
//   },
//   extraReducers: (b) => {
//     b
//       // create
//       .addCase(createVisitReport.pending, (s) => {
//         s.submitting = true;
//         s.error = null;
//       })
//       .addCase(createVisitReport.fulfilled, (s, a) => {
//         s.submitting = false;
//         s.myList = [a.payload, ...s.myList];
//       })
//       .addCase(createVisitReport.rejected, (s, a) => {
//         s.submitting = false;
//         s.error = a.payload;
//       })

//       // my history
//       .addCase(fetchMyReports.pending, (s) => {
//         s.loading = true;
//         s.error = null;
//       })
//       .addCase(fetchMyReports.fulfilled, (s, a) => {
//         s.loading = false;
//         s.myList = a.payload.items || [];
//         s.myTotal = a.payload.total ?? (a.payload.items || []).length;
//       })
//       .addCase(fetchMyReports.rejected, (s, a) => {
//         s.loading = false;
//         s.error = a.payload;
//       })

//       // all / team
//       .addCase(fetchAllReports.pending, (s) => {
//         s.loading = true;
//         s.error = null;
//       })
//       .addCase(fetchAllReports.fulfilled, (s, a) => {
//         s.loading = false;
//         s.allList = a.payload.items || [];
//         s.allTotal = a.payload.total ?? (a.payload.items || []).length;
//       })
//       .addCase(fetchAllReports.rejected, (s, a) => {
//         s.loading = false;
//         s.error = a.payload;
//       })

//       // per-executive summary
//       .addCase(fetchExecutiveSummary.pending, (s) => {
//         s.summaryLoading = true;
//       })
//       .addCase(fetchExecutiveSummary.fulfilled, (s, a) => {
//         s.summaryLoading = false;
//         s.execSummary = a.payload;
//       })
//       .addCase(fetchExecutiveSummary.rejected, (s, a) => {
//         s.summaryLoading = false;
//         s.error = a.payload;
//       })

//       // by id
//       .addCase(fetchReportById.fulfilled, (s, a) => {
//         s.selected = a.payload;
//       })

//       // update
//       .addCase(updateVisitReport.fulfilled, (s, a) => {
//         s.selected = a.payload;
//         const i = s.myList.findIndex((x) => x._id === a.payload._id);
//         if (i !== -1) s.myList[i] = a.payload;
//         const j = s.allList.findIndex((x) => x._id === a.payload._id);
//         if (j !== -1) s.allList[j] = a.payload;
//       })

//       // delete
//       .addCase(deleteVisitReport.fulfilled, (s, a) => {
//         s.myList = s.myList.filter((x) => x._id !== a.payload);
//         s.allList = s.allList.filter((x) => x._id !== a.payload);
//       });
//   },
// });

// export const { clearVisitReportError, clearSelected } = slice.actions;
// export default slice.reducer;
